import React, { useEffect } from 'react';
import { Divider, Stack } from '@mui/material';
import { useWatch, useFormContext } from 'react-hook-form';
import { TagSelector, SomethingElseField } from '../FormFields';
import {
    ReviewFormValues,
    StepWithTags,
    TagsByStep,
    EMPTY_TAGSET,
} from '../types';
import { asPath } from '../utils';

interface ConditionalTagsProps {
    step: StepWithTags;
    positiveLabel: string;
    negativeLabel: string;
    tags: TagsByStep;
}

const ConditionalTags: React.FC<ConditionalTagsProps> = ({
    step,
    positiveLabel,
    negativeLabel,
    tags,
}) => {
    const { control, setValue, getValues } = useFormContext<ReviewFormValues>();
    const rating =
        useWatch({
            control,
            name: asPath(`${step}.rating` as any),
        }) || 0;

    const isFive = rating === 5;
    const isLow = rating > 0 && rating <= 3;
    const isFour = rating === 4;

    const posPath = asPath(`${step}.positives` as any);
    const negPath = asPath(`${step}.negatives` as any);
    const posOtherPath = asPath(`${step}.otherTextPositive` as any);
    const negOtherPath = asPath(`${step}.otherTextNegative` as any);

    const showPos = isFive || isFour;
    const showNeg = isLow || isFour;

    const stepTags = (tags && tags[step]) || EMPTY_TAGSET;
    const positiveOptions = stepTags.positive || [];
    const negativeOptions = stepTags.negative || [];

    useEffect(() => {
        if (!showPos) {
            setValue(posPath, [], { shouldDirty: true, shouldValidate: true });
            setValue(posOtherPath, '', {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
        if (!showNeg) {
            setValue(negPath, [], { shouldDirty: true, shouldValidate: true });
            setValue(negOtherPath, '', {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    }, [
        showPos,
        showNeg,
        getValues,
        setValue,
        posPath,
        negPath,
        posOtherPath,
        negOtherPath,
    ]);

    return (
        <Stack spacing={2}>
            {(showPos || showNeg) && <Divider />}
            {showPos && (
                <>
                    <TagSelector
                        name={posPath}
                        options={positiveOptions}
                        label={`${positiveLabel}${isFour ? ' (optional)' : ''}`}
                        helper='Pick any that apply.'
                    />
                    <SomethingElseField
                        name={posOtherPath}
                        dependsOn={posPath}
                        label='What stood out?'
                    />
                </>
            )}
            {showNeg && (
                <>
                    <TagSelector
                        name={negPath}
                        options={negativeOptions}
                        label={`${negativeLabel}${isFour ? ' (optional)' : ''}`}
                        helper='Pick any that apply.'
                    />
                    <SomethingElseField
                        name={negOtherPath}
                        dependsOn={negPath}
                        label='What was the issue?'
                    />
                </>
            )}
        </Stack>
    );
};

export default ConditionalTags;
